<template>
  <div>
    <form @submit.prevent="doSubmit" autocomplete="new-password">
      <validation-observer ref="observer">
        <template v-if="page === PAGE.REQUEST">
          <p class="is-size-3 has-text-weight-bold">
            {{ $t("mp:forgotten_password.title") }}
          </p>

          <p class="info has-text-grey-icon">
            {{ $t("mp:forgotten_password.subtitle") }}
          </p>

          <BInputWithValidation
            v-model="user"
            rules="required"
            :label="$t('mp:forgotten_password.input.phone_or_email.label')"
            label-position="on-border"
            class="mb-5"
          />

          <b-field>
            <b-button
              type="is-blue"
              native-type="submit"
              :loading="reqLoading"
              expanded
              >{{ $t("mp:forgotten_password.button.next") }}
            </b-button>
          </b-field>
        </template>

        <template v-else-if="page === PAGE.SOLUTION">
          <template v-if="looksLikeAnEmail && !this.forcePasswordChange">
            <p class="is-size-3 has-text-weight-bold">
              {{ $t("mp:forgotten_password.email.title") }}
            </p>

            <p class="info has-text-grey-icon">
              {{ $t("mp:forgotten_password.email.subtitle") }}
            </p>
          </template>

          <template v-else>
            <p class="is-size-3 has-text-weight-bold">
              {{ $t("mp:forgotten_password.phone.title") }}
            </p>

            <p class="info has-text-grey-icon" v-if="this.forcePasswordChange">
              {{ $t("mp:forgotten_password.password_change_required.title") }}
            </p>
          </template>

          <BInputWithValidation
            :label="$t('mp:forgotten_password.input.security_code.label')"
            rules="required"
            label-position="on-border"
            v-model="code"
          />

          <b-field
            :label="$t('mp:forgotten_password.input.new_password.label')"
            rules="required"
            label-position="on-border"
          >
            <PasswordCheckerInput
              v-model="newPassword"
              @validated="passwordValid = $event"
            />
          </b-field>

          <b-field>
            <b-button
              type="is-blue"
              native-type="submit"
              expanded
              :loading="finishLoading"
              >{{ $t("mp:forgotten_password.button.submit") }}
            </b-button>
          </b-field>
        </template>
      </validation-observer>
    </form>
  </div>
</template>

<script>
const PAGE = {
  REQUEST: 0,
  SOLUTION: 1,
};

import i18n from "@/plugins/i18n";
import { mapGetters } from "vuex";
import { generateNonce } from "@/utils/util";
import Vue from "vue";

export default {
  name: "PasswordReset",
  title: "mp:page.forgot_password.title",
  components: {
    BInputWithValidation: () =>
      import("@/components/input/BInputWithValidation.vue"),
    PasswordCheckerInput: () =>
      import("@/components/input/PasswordCheckerInput.vue"),
  },
  data() {
    return {
      PAGE,
      user: "",
      nonce: generateNonce(),
      code: "",
      chId: "",
      newPassword: "",
      passwordValid: false,
      page: PAGE.REQUEST,
      reqLoading: false,
      finishLoading: false,
      forcePasswordChange: false,
    };
  },
  mounted() {
    if (this.loggedIn) {
      this.$router.push("/user/profile");
    }
  },
  computed: {
    ...mapGetters({
      loggedIn: "session/isReady",
      secondFactor: "session/secondFactor",
    }),
    looksLikeAnEmail() {
      return this.user.includes("@");
    },
  },
  created() {
    // parse URL from the verification e-mail
    const code = this.$route.query.code;
    if (code) {
      this.code = code;
      this.page = PAGE.SOLUTION;
    }
    // Required password change
    if (this.secondFactor) {
      this.page = PAGE.SOLUTION;
      this.chId = this.secondFactor.id;
      this.nonce = this.secondFactor.nonce;
      this.forcePasswordChange = true;
      this.$store.dispatch("session/deleteSecondFactor");
    }
  },
  methods: {
    async doSubmit() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      if (!this.user.includes("@")) {
        let tel = this.user.replace(/ /g, "");
        tel = tel.replace("+", "");
        if (tel.length === 9) {
          // se 06, se 36
          tel = "36" + tel;
        }
        if (tel.startsWith("06")) {
          // 06 -> 36
          tel = "36" + tel.substring(2);
        }
        Vue.set(this, "user", tel);
      }

      if (this.page === PAGE.REQUEST) {
        try {
          this.reqLoading = true;
          let reqResponse = await this.$store.dispatch(
            "session/requestPwdChange",
            {
              user: this.user,
              nonce: this.nonce,
            }
          );
          this.chId = reqResponse.id;

          this.reqLoading = false;
          this.page = PAGE.SOLUTION;
        } catch (ex) {
          console.error(ex);
          this.$buefy.toast.open({
            message: i18n.t("mp:forget.error.general"),
            type: "is-danger",
          });
          this.reqLoading = false;
        }
      } else {
        if (!this.passwordValid) return;

        try {
          this.finishLoading = true;

          let chngResponse = await this.$store.dispatch(
            "session/finishPwdChange",
            {
              id: this.chId,
              nonce: this.nonce,
              code: this.code,
              newPassword: this.newPassword,
            }
          );
          let redirect = await this.$store.dispatch(
            "profile/setStatus",
            chngResponse
          );

          this.$router.push(redirect);
          this.finishLoading = false;
        } catch (ex) {
          console.error(ex);
          this.finishLoading = false;
          if (ex.response.status === 400) {
            this.$buefy.toast.open({
              message: i18n.t("mp:forget.new_password_cannot_be_old_password"),
              type: "is-danger",
            });
          } else {
            this.$buefy.toast.open({
              message: i18n.t("mp:forget.error.general"),
              type: "is-danger",
            });
          }
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.info {
  margin-top: -1rem;
  margin-bottom: 2rem !important;
  max-width: 75%;
}
</style>
